<template>
  <VendorBlock
    id="rating"
    class="vendor-reviews"
  >
    <template #title>
      <span class="vendor-reviews__title">
        Отзывы от пользователей платформы
      </span>

      <span class="vendor-reviews__rating">
        <svg-icon
          name="basic/ui-star-filled"
          width="24"
          height="24"
          aria-label="Общий рейтинг:"
          class="vendor-reviews__star"
        />

        {{ rating }}
      </span>
    </template>

    <template #content>
      <div class="vendor-reviews__content">
        <ul class="vendor-reviews__list">
          <li
            v-for="(review, i) in slicedReviews"
            :key="i"
            class="vendor-reviews__item"
          >
            <UiReviewCard
              :user="review.author"
              :review="review.review"
              class="vendor-reviews__card"
            />
          </li>
        </ul>

        <UiButton
          v-if="showMoreButton"
          theme="quaternary"
          size="big"
          stretch
          @click="isExpanded = !isExpanded"
        >
          {{ isExpanded ? 'Скрыть' : 'Показать ещё' }}
        </UiButton>
      </div>
    </template>
  </VendorBlock>
</template>

<script setup lang="ts">
  import UiReviewCard from '@academica-box/components/Cards/UiReviewCard/UiReviewCard.vue'
  import UiButton from '@academica-box/components/Buttons/UiButton/UiButton.vue'
  import type { VendorReview } from '@/types/vendor'
  import VendorBlock from '@/components/Vendor/VendorBlock.vue'

  const props = defineProps<{
    reviews: VendorReview[];
    rating: number;
  }>()

  const viewport = useViewport()

  const isExpanded = ref(false)

  const isMobile = computed(() => viewport.isLessThan('lg'))

  const showMoreButton = computed(() => {
    const showMobile = isMobile.value && props.reviews.length > 2
    const showDesktop = !isMobile.value && props.reviews.length > 4

    return showMobile || showDesktop
  })

  const slicedReviews = computed(() => {
    const cardsMobile = [...props.reviews].splice(0, 2)
    const cardsDesktop = [...props.reviews].splice(0, 4)

    return isExpanded.value
      ? props.reviews
      : isMobile.value
        ? cardsMobile
        : cardsDesktop
  })
</script>

<style lang="scss">
  @use '@academica-box/utils/styles/mixins/UiMedia' as *;
  @use '@academica-box/utils/styles/mixins/UiReset' as *;

  .vendor-reviews__title {
    display: block;
    width: 209px;
    margin-bottom: 12px;

    @include media(md) {
      width: 307px;
    }

    @include media(lg) {
      width: unset;
      margin-bottom: 24px;
    }
  }

  .vendor-reviews__rating {
    display: flex;
    align-items: center;
  }

  .vendor-reviews__star {
    width: 24px;
    height: 24px;
    margin-right: 4px;
    color: var(--accent-rating-color);

    @include media(lg) {
      width: 40px;
      height: 40px;
      margin-right: 8px;
    }
  }

  .vendor-reviews__list {
    @include ulres;

    margin-bottom: 20px;

    @include media(md) {
      display: grid;
      grid-gap: 6px;
      grid-template-columns: 1fr 1fr;
    }

    @include media(lg) {
      margin-bottom: 32px;
    }

    @include media(lg, xl-max) {
      display: block;
    }
  }

  .vendor-reviews__item {
    &:not(:last-child) {
      @include media(sm-max) {
        margin-bottom: 6px;
      }

      @include media(lg, xl-max) {
        margin-bottom: 6px;
      }
    }
  }

  .vendor-reviews__card {
    height: 100%;
  }
</style>
